// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The merge engine.
 *
 * Apply a plan — a specific set of differences the user chose — to Board A, and
 * produce a new file. Neither input is modified, in memory or on disk.
 *
 * What changed when this stopped being a region splice: the *selection* got
 * finer, and nothing else did. Net re-indexing by name, UUID collision handling,
 * group member rewiring, zone-fill invalidation, output ordering and the
 * post-merge self-check are all unchanged, because none of them ever cared which
 * objects were being moved — only that they were being moved correctly.
 *
 * The governing rule for this module is still the one it was written under:
 * where an operation must *abort*, it aborts, and does not "helpfully" attempt
 * recovery. Silent corruption of a PCB file is the worst possible failure mode
 * of this tool, far worse than refusing to run.
 */

import {
  atom,
  cloneNode,
  getUuid,
  isNode,
  node,
  removeChildren,
  serialize,
  setUuid,
  walk,
  type Node,
} from '../sexp/index.ts';
import { Board } from '../model/board.ts';
import { BOARD_PROPERTY_TOKEN } from '../model/objects.ts';
import type { DiffResult } from '../diff/diff.ts';
import { GLOBAL_BLOCK_INFO, type BuiltPlan, type GlobalBlockId, type MergePlan } from '../select/plan.ts';
import { checkPlan, type PlanCheck, type PlanIssue } from '../verify/plan.ts';
import { defaultNewUuid, describeNetRemap, NetRemapError, remapNets, type NetRemapStats } from './netmap.ts';
import { selfCheck, type SelfCheckResult } from './selfcheck.ts';

/**
 * Confirmations are an explicit checkbox *plus* this string, typed. Two gestures,
 * because one is a reflex and two is a decision.
 */
export const OVERRIDE_CONFIRMATION = 'I ACCEPT THE RISK';

export interface MergeConfirmation {
  /** The explicit checkbox. */
  acknowledged: boolean;
  /** Must equal {@link OVERRIDE_CONFIRMATION}, typed by the user. */
  confirmation: string;
}

export interface MergeOptions {
  /** The plan, already closed over its referential dependencies. */
  built: BuiltPlan;
  /** The diff the plan was built from. */
  diff: DiffResult;
  /** Reuse an existing plan check instead of recomputing it. */
  check?: PlanCheck;
  /** Satisfies outstanding confirmations. Blockers can never be confirmed away. */
  confirm?: MergeConfirmation;
  /** Output file name. Defaults to `<Board A name>-merged.kicad_pcb`. */
  outputName?: string;
  /** UUID source, injectable so tests are deterministic. */
  newUuid?: () => string;
}

export interface MergeStats {
  /** Objects deleted from Board A. */
  removedFromA: Record<string, number>;
  /** Objects brought over from Board B. */
  addedFromB: Record<string, number>;
  /** UUIDs regenerated because of a collision with Board A. */
  uuidCollisions: number;
  netReferencesRemapped: number;
  /** Net names added to Board A's table so spliced copper has somewhere to attach. */
  netsAdded: string[];
  /** Nets that arrived with the objects naming them, where nets are named inline. */
  netsIntroduced: string[];
  zonesStripped: number;
  /** Board-level blocks taken from Board B. */
  globalsTaken: GlobalBlockId[];
}

export interface MergeResult {
  /** The merged file's text, ready to download. */
  text: string;
  fileName: string;
  /** Plain-text report for a commit message. */
  report: string;
  /** True when any zone was touched: the user must run Fill All Zones. */
  needsZoneRefill: boolean;
  stats: MergeStats;
  /** Issues the user explicitly confirmed, recorded for the report. */
  confirmed: PlanIssue[];
  selfCheck: SelfCheckResult;
  check: PlanCheck;
}

/** Thrown whenever the merge refuses to produce a file. */
export class MergeAbort extends Error {
  readonly reasons: string[];
  readonly check?: PlanCheck;
  readonly selfCheck?: SelfCheckResult;

  constructor(message: string, reasons: string[], extra: { check?: PlanCheck; selfCheck?: SelfCheckResult } = {}) {
    super(message);
    this.name = 'MergeAbort';
    this.reasons = reasons;
    if (extra.check) this.check = extra.check;
    if (extra.selfCheck) this.selfCheck = extra.selfCheck;
  }
}

export function merge(a: Board, b: Board, options: MergeOptions): MergeResult {
  const { built, diff } = options;
  const plan = built.plan;
  const newUuid = options.newUuid ?? defaultNewUuid;

  // --- step 1: the gate ------------------------------------------------------
  const check = options.check ?? checkPlan(a, b, diff, built);

  if (check.blockers.length > 0) {
    throw new MergeAbort(
      `Merge refused: ${check.blockers.length} problem(s) would produce a file KiCad may not load.`,
      check.blockers.map((i) => i.title),
      { check },
    );
  }

  if (plan.take.size === 0 && plan.takeGlobal.size === 0) {
    throw new MergeAbort('Merge refused: nothing is selected, so there is nothing to merge.', [], { check });
  }

  let confirmed: PlanIssue[] = [];
  if (check.confirmations.length > 0) {
    const c = options.confirm;
    if (!c?.acknowledged || c.confirmation.trim() !== OVERRIDE_CONFIRMATION) {
      throw new MergeAbort(
        `Merge refused: ${check.confirmations.length} action(s) need explicit confirmation. ` +
          `Tick the box and type "${OVERRIDE_CONFIRMATION}".`,
        check.confirmations.map((i) => i.title),
        { check },
      );
    }
    confirmed = check.confirmations;
  }

  // --- step 2: work out which objects go and which come ----------------------
  const doomed = new Set<number>();
  const incoming: Array<{ node: Node; type: string }> = [];
  const removedFromA: Record<string, number> = {};
  const addedFromB: Record<string, number> = {};

  for (const change of diff.substantive) {
    if (!plan.take.has(change.key)) continue;
    if (change.a !== undefined) {
      doomed.add(change.a.index);
      removedFromA[change.a.type] = (removedFromA[change.a.type] ?? 0) + 1;
    }
    if (change.b !== undefined) {
      incoming.push({ node: change.b.node, type: change.b.type });
    }
  }

  // Work on a clone; the caller's Board must remain valid and untouched.
  const outRoot = cloneNode(a.root);
  const survivors = outRoot.children.filter((_, i) => !doomed.has(i));

  // --- step 3: board-level blocks --------------------------------------------
  const netsAdded = plan.takeGlobal.has('nets') ? addMissingNets(survivors, a, b, diff, plan) : [];
  for (const id of plan.takeGlobal) {
    if (id === 'nets') continue;
    if (id === 'properties') replaceProperties(survivors, b);
    else replaceBlock(survivors, id, b);
  }

  // --- step 4: bring Board B's objects across --------------------------------
  const netStats: NetRemapStats = { rewritten: 0, names: new Set<string>(), introduced: new Set<string>() };
  const clones: Node[] = [];
  let zonesStripped = 0;

  // Every UUID the output still uses, at any depth — a collision inside a
  // footprint is as capable of confusing KiCad as one at board level.
  const takenUuids = new Set<string>();
  for (const child of survivors) {
    if (!isNode(child)) continue;
    for (const n of walk(child)) {
      const u = getUuid(n);
      if (u !== undefined && u !== '') takenUuids.add(u);
    }
  }

  const uuidRewrites = new Map<string, string>();
  let uuidCollisions = 0;

  // Nets resolve against the output's table, which the previous step may have
  // grown. Re-parsing is the honest way to ask what the output actually says.
  const target = netsAdded.length > 0 ? Board.parse(serialize({ ...outRoot, children: survivors }), a.fileName) : a;

  for (const item of incoming) {
    const clone = cloneNode(item.node);

    // Net indices, by name. Any failure here aborts the whole merge.
    try {
      remapNets(clone, b, target, netStats);
    } catch (err) {
      if (err instanceof NetRemapError) {
        throw new MergeAbort('Merge aborted while re-indexing nets. No output was produced.', [err.message], { check });
      }
      throw err;
    }

    // Zone fill caches are stale the moment anything near them changes.
    if (clone.name === 'zone') {
      const stripped =
        removeChildren(clone, 'filled_polygon') + removeChildren(clone, 'fill_segments') + removeChildren(clone, 'filled_areas');
      if (stripped > 0) zonesStripped++;
    }

    // UUID collisions. A footprint's `path` is never touched — that is the
    // schematic link and rewriting it would orphan the component.
    //
    // Collisions are resolved per *distinct* UUID rather than per node, because
    // KiCad legitimately shares one UUID between an object and a child of it — a
    // `dimension` and its nested `gr_text` do exactly this. Rewriting node by
    // node would see the second occurrence as colliding with the first and split
    // a deliberate pairing, so a spliced dimension would come out subtly altered.
    const own = new Set<string>();
    for (const n of walk(clone)) {
      const u = getUuid(n);
      if (u !== undefined && u !== '') own.add(u);
    }
    const localRewrites = new Map<string, string>();
    for (const u of own) {
      if (!takenUuids.has(u)) {
        takenUuids.add(u);
        continue;
      }
      const replacement = newUuid();
      localRewrites.set(u, replacement);
      uuidRewrites.set(u, replacement);
      takenUuids.add(replacement);
      uuidCollisions++;
    }
    if (localRewrites.size > 0) {
      for (const n of walk(clone)) {
        const u = getUuid(n);
        if (u === undefined) continue;
        const replacement = localRewrites.get(u);
        if (replacement !== undefined) setUuid(n, replacement);
      }
    }

    clones.push(clone);
    addedFromB[item.type] = (addedFromB[item.type] ?? 0) + 1;
  }

  // --- step 5: member references are translated into the output's UUID space --
  //
  // Two different translations, and both are needed.
  //
  // A container arriving from Board B names its members by *Board B's* UUIDs.
  // For a member that is also being spliced, that UUID comes across with it and
  // the reference still resolves — unless a collision forced it to be
  // regenerated, which `uuidRewrites` records.
  //
  // For a member that is *not* being spliced, Board A keeps its own copy of that
  // object, which may carry a different UUID. Left alone, the reference would
  // either dangle or — far worse — resolve to whatever Board A happens to use
  // that UUID for, silently putting a different object in the group.
  const memberRewrites = new Map<string, string>();
  for (const change of diff.changes) {
    const from = change.b?.uuid;
    const to = change.a?.uuid;
    if (from === undefined || to === undefined || from === '' || to === '' || from === to) continue;
    if (plan.take.has(change.key)) continue; // it is coming across; its own UUID applies
    memberRewrites.set(from, to);
  }

  if (uuidRewrites.size > 0 || memberRewrites.size > 0) {
    for (const clone of clones) {
      for (const n of walk(clone)) {
        if (n.name !== 'members') continue;
        for (const child of n.children) {
          if (child.kind !== 'atom') continue;
          const replacement = uuidRewrites.get(child.value) ?? memberRewrites.get(child.value);
          if (replacement !== undefined) child.value = replacement;
        }
      }
    }
  }

  // --- assemble, keeping like tokens together for readable git diffs ---------
  outRoot.children = spliceInOrder(survivors, clones);

  // --- serialize --------------------------------------------------------------
  const text = serialize(outRoot);
  const fileName = options.outputName ?? defaultOutputName(a.fileName);

  // --- post-merge self-check --------------------------------------------------
  let merged: Board;
  try {
    merged = Board.parse(text, fileName);
  } catch (err) {
    throw new MergeAbort(
      'Merge produced a file that does not parse. This is a bug in the tool; the output has been discarded.',
      [(err as Error).message],
      { check },
    );
  }

  const expectedDelta: Record<string, number> = {};
  for (const [type, n] of Object.entries(removedFromA)) expectedDelta[type] = (expectedDelta[type] ?? 0) - n;
  for (const [type, n] of Object.entries(addedFromB)) expectedDelta[type] = (expectedDelta[type] ?? 0) + n;

  const result = selfCheck({ a, b, merged, plan, expectedDelta });
  if (!result.ok) {
    throw new MergeAbort(
      'Post-merge self-check FAILED. The output has been discarded rather than offered to you. ' +
        'This is a bug in the tool, not a problem with your files — please report it with the assertions below.',
      result.failures.map((f) => `${f.assertion} — ${f.detail}`),
      { check, selfCheck: result },
    );
  }

  const stats: MergeStats = {
    removedFromA,
    addedFromB,
    uuidCollisions,
    netReferencesRemapped: netStats.rewritten,
    netsAdded,
    netsIntroduced: [...netStats.introduced].sort(),
    zonesStripped,
    globalsTaken: [...plan.takeGlobal],
  };
  const needsZoneRefill = zonesStripped > 0 || (addedFromB['zone'] ?? 0) > 0 || (removedFromA['zone'] ?? 0) > 0;

  return {
    text,
    fileName,
    report: formatMergeReport({ a, b, stats, confirmed, needsZoneRefill, netStats, selfCheck: result, fileName, check }),
    needsZoneRefill,
    stats,
    confirmed,
    selfCheck: result,
    check,
  };
}

/**
 * Add the net names the plan needs to Board A's table.
 *
 * Additive on purpose. Replacing the table with Board B's would renumber every
 * net, and since Board A's own objects reference nets by index, that would
 * silently reattach copper all over a board nobody asked to change.
 */
function addMissingNets(survivors: Node['children'], a: Board, b: Board, diff: DiffResult, plan: MergePlan): string[] {
  // KiCad 10 names nets inline and has no table to extend.
  if (a.usesNamedNets) return [];

  const needed = new Set<string>();
  for (const change of diff.substantive) {
    if (!plan.take.has(change.key) || change.b === undefined) continue;
    for (const name of netNamesUsedBy(change.b.node, b)) {
      if (name !== '' && !a.netIndexByName.has(name)) needed.add(name);
    }
  }
  if (needed.size === 0) return [];

  let next = 0;
  let lastNetAt = -1;
  survivors.forEach((child, i) => {
    if (!isNode(child) || child.name !== 'net') return;
    lastNetAt = i;
    const index = Number(child.children.find((c) => c.kind === 'atom')?.value);
    if (Number.isFinite(index)) next = Math.max(next, index);
  });

  const added = [...needed].sort();
  const nodes = added.map((name) => node('net', [atom(String(++next), false), atom(name, true)]));
  survivors.splice(lastNetAt + 1, 0, ...nodes);
  return added;
}

/** Net names a subtree references, resolved through its own board's table. */
function netNamesUsedBy(subtree: Node, board: Board): string[] {
  const out: string[] = [];
  for (const n of walk(subtree)) {
    if (n.name !== 'net') continue;
    const first = n.children.find((c) => c.kind === 'atom');
    if (first === undefined || first.kind !== 'atom') continue;
    if (first.quoted) {
      out.push(first.value);
      continue;
    }
    const index = Number(first.value);
    const named = n.children.filter((c) => c.kind === 'atom')[1];
    if (named !== undefined && named.kind === 'atom') out.push(named.value);
    else if (Number.isFinite(index)) {
      const name = board.netNameByIndex.get(index);
      if (name !== undefined) out.push(name);
    }
  }
  return out;
}

/**
 * Replace Board A's text variables with Board B's.
 *
 * These do not live in one block. KiCad has written them as bare top-level
 * `(property "NAME" "VALUE")` nodes and as children of a `(properties …)` block
 * depending on version, and a file can carry both. Replacing only the block form
 * would leave the loose ones behind and merge the two sets by accident.
 */
function replaceProperties(survivors: Node['children'], b: Board): void {
  let insertAt = -1;
  for (let i = survivors.length - 1; i >= 0; i--) {
    const child = survivors[i]!;
    if (!isNode(child)) continue;
    if (child.name !== BOARD_PROPERTY_TOKEN && child.name !== 'properties') continue;
    insertAt = i;
    survivors.splice(i, 1);
  }

  const incoming: Node[] = [];
  for (const child of b.root.children) {
    if (!isNode(child)) continue;
    if (child.name === BOARD_PROPERTY_TOKEN || child.name === 'properties') incoming.push(cloneNode(child));
  }
  if (incoming.length === 0) return;

  if (insertAt === -1) {
    const anchor = survivors.findIndex((c) => isNode(c) && c.name === 'paper');
    insertAt = anchor === -1 ? survivors.length : anchor + 1;
  }
  survivors.splice(insertAt, 0, ...incoming);
}

/** Replace one board-level block with Board B's version. */
function replaceBlock(survivors: Node['children'], id: GlobalBlockId, b: Board): void {
  const from = b.globalBlocks.get(id);
  const index = survivors.findIndex((c) => isNode(c) && c.name === id);

  if (from === undefined) {
    if (index !== -1) survivors.splice(index, 1);
    return;
  }
  const clone = cloneNode(from);
  if (index === -1) {
    // Board A has no such block; put it where a board file normally keeps one.
    const anchor = survivors.findIndex((c) => isNode(c) && c.name === 'general');
    survivors.splice(anchor === -1 ? 1 : anchor + 1, 0, clone);
  } else {
    survivors[index] = clone;
  }
}

/**
 * Insert the cloned nodes after the last surviving node of the same token, so
 * footprints stay with footprints and tracks with tracks. Purely cosmetic — it
 * keeps the resulting git diff readable.
 */
function spliceInOrder(survivors: Node['children'], clones: Node[]): Node['children'] {
  if (clones.length === 0) return survivors;

  const byType = new Map<string, Node[]>();
  for (const c of clones) {
    let list = byType.get(c.name);
    if (!list) byType.set(c.name, (list = []));
    list.push(c);
  }

  const lastIndexOfType = new Map<string, number>();
  survivors.forEach((child, i) => {
    if (isNode(child)) lastIndexOfType.set(child.name, i);
  });

  const out: Node['children'] = [];
  const emitted = new Set<string>();
  survivors.forEach((child, i) => {
    out.push(child);
    if (!isNode(child)) return;
    if (lastIndexOfType.get(child.name) !== i) return;
    const pending = byType.get(child.name);
    if (pending) {
      out.push(...pending);
      emitted.add(child.name);
    }
  });

  // Types with no surviving example go at the end, in a stable order.
  for (const [type, list] of [...byType].sort(([x], [y]) => (x < y ? -1 : x > y ? 1 : 0))) {
    if (emitted.has(type)) continue;
    out.push(...list);
  }

  return out;
}

export function defaultOutputName(boardAName: string): string {
  const base = boardAName.replace(/\.kicad_pcb$/i, '');
  return `${base}-merged.kicad_pcb`;
}

// --- report -------------------------------------------------------------------

interface ReportInput {
  a: Board;
  b: Board;
  stats: MergeStats;
  confirmed: PlanIssue[];
  needsZoneRefill: boolean;
  netStats: NetRemapStats;
  selfCheck: SelfCheckResult;
  check: PlanCheck;
  fileName: string;
}

/**
 * Plain-text merge report the user can paste into a commit message: what was
 * taken, what was left, and what needs doing afterwards.
 */
export function formatMergeReport(input: ReportInput): string {
  const { a, b, stats, confirmed, needsZoneRefill, netStats, selfCheck: result, check, fileName } = input;
  const lines: string[] = [];

  lines.push('PCB merge');
  lines.push('');
  lines.push(`Board A : ${a.fileName}`);
  lines.push('          the base — everything not selected comes from here');
  lines.push(`Board B : ${b.fileName}`);
  lines.push('          the selected differences are taken from here');
  lines.push(`Output  : ${fileName}`);
  lines.push('');

  const types = [...new Set([...Object.keys(stats.removedFromA), ...Object.keys(stats.addedFromB)])].sort();
  lines.push('Objects changed');
  if (types.length === 0) {
    lines.push('  (none — only board-level state was taken)');
  } else {
    for (const t of types) {
      const removed = stats.removedFromA[t] ?? 0;
      const added = stats.addedFromB[t] ?? 0;
      const delta = added - removed;
      lines.push(`  ${t.padEnd(14)} −${removed}  +${added}` + (delta === 0 ? '' : `  (net ${delta > 0 ? '+' : ''}${delta})`));
    }
  }
  lines.push('');

  if (check.effects.unselected > 0) {
    lines.push('NOT merged');
    lines.push(`  ${check.effects.unselected} difference(s) were in no selection. Board A keeps its own version of each.`);
    lines.push('');
  }

  lines.push('Integrity');
  lines.push(`  ${describeNetRemap(netStats)}`);
  if (stats.netsIntroduced.length > 0) {
    lines.push(
      `  ${stats.netsIntroduced.length} net(s) came into existence with the objects naming them: ` +
        `${stats.netsIntroduced.slice(0, 6).join(', ')}${stats.netsIntroduced.length > 6 ? '…' : ''}.`,
    );
  }
  if (stats.netsAdded.length > 0) {
    lines.push(`  ${stats.netsAdded.length} net(s) added to Board A's table: ${stats.netsAdded.join(', ')}.`);
  }
  lines.push(
    stats.uuidCollisions === 0
      ? '  No UUID collisions.'
      : `  ${stats.uuidCollisions} UUID(s) regenerated to avoid collisions with Board A.`,
  );
  lines.push(`  Post-merge self-check: ${result.ok ? 'passed' : 'FAILED'}.`);
  if (result.preExistingDuplicateUuids.length > 0) {
    lines.push(
      `  Note: ${result.preExistingDuplicateUuids.length} UUID(s) were already shared by more than one ` +
        `object in the source files and still are. KiCad does this itself (a dimension and its text share ` +
        `a UUID), so this is carried through rather than "fixed".`,
    );
  }
  lines.push('');

  if (stats.globalsTaken.length > 0) {
    lines.push('Board-level state taken from Board B');
    for (const id of stats.globalsTaken) lines.push(`  ${GLOBAL_BLOCK_INFO[id].label}`);
    lines.push('');
  } else {
    lines.push('Board-level state (stackup, netclasses, design rules, text variables)');
    lines.push(`  Carried through from Board A (${a.fileName}) unchanged.`);
    lines.push('');
  }

  if (confirmed.length > 0) {
    lines.push(`Confirmed by the operator (${confirmed.length}):`);
    for (const i of confirmed) lines.push(`  ${i.title}`);
    lines.push('');
  }

  if (needsZoneRefill) {
    lines.push('ACTION REQUIRED');
    lines.push('  Zone fill caches were discarded, because a fill computed against different surroundings');
    lines.push('  is wrong and looks right.');
    lines.push('  Open the merged file in KiCad and run Edit → Fill All Zones before plotting or ordering.');
    lines.push('');
  }

  return lines.join('\n') + '\n';
}
